/* @jsx h */
import { defineComponent, h, onMounted, ref, watch } from "vue";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type {
  InvoiceDraft,
  InvoiceFilter,
  InvoiceSummary,
  InvoiceTotals,
} from "./types";
import { daysUntil, formatDay, money } from "./format";
import { InvoiceEditor } from "./InvoiceEditor";
import { MarkPaidModal, type Payment } from "./MarkPaidModal";
import { SummaryTiles } from "./SummaryTiles";
import {
  ScanDialog,
  scanToInvoice,
  useScanRouting,
  type ScanResult,
} from "../Scan/index";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

const FILTERS: InvoiceFilter[] = [null, "open", "overdue", "paid", "void"];

const EMPTY_TOTALS: InvoiceTotals = {
  open_total: 0,
  open_count: 0,
  overdue_total: 0,
  overdue_count: 0,
  due_soon_total: 0,
  paid_30d_total: 0,
};

// Supplier invoices: what's owed, what's overdue, mark paid.
// See docs/plans/inventory_and_costing/05_purchasing.md.
export const InventoryInvoicesPage = defineComponent({
  name: "InventoryInvoicesPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const filter = ref<InvoiceFilter>(null);
    const invoices = ref<InvoiceSummary[]>([]);
    const totals = ref<InvoiceTotals>(EMPTY_TOTALS);
    // undefined = closed, null = new invoice, number = open that invoice.
    const editing = ref<number | null | undefined>(undefined);
    const draft = ref<InvoiceDraft | null>(null);
    const paying = ref<InvoiceSummary | null>(null);
    const busy = ref(false);

    const { scanning, onOpen } = useScanRouting(
      "invoice",
      (result: ScanResult) => {
        draft.value = scanToInvoice(result);
        editing.value = null;
      },
    );

    const load = async () => {
      const data = await trpc.supplierInvoices.list.query({
        filter: filter.value,
        limit: 200,
        offset: 0,
      });
      invoices.value = data.invoices as InvoiceSummary[];
      totals.value = data.summary;
    };

    const reload = async () => {
      try {
        await load();
      } catch {
        toast.showToast(t("inventory.invoices.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    };
    onMounted(reload);
    watch(filter, reload);

    const pay = async (payment: Payment) => {
      if (!paying.value) return;
      busy.value = true;
      try {
        await trpc.supplierInvoices.markPaid.mutate({
          id: paying.value.id,
          ...payment,
        });
        toast.showToast(t("inventory.invoices.marked_paid"), {
          type: "success",
        });
        paying.value = null;
        await load();
      } catch (e) {
        toast.showToast(
          (e as { message?: string })?.message ??
            t("inventory.invoices.save_failed"),
          { type: "error" },
        );
      } finally {
        busy.value = false;
      }
    };

    const dueCell = (inv: InvoiceSummary) => {
      if (!inv.due_date) return <span class="text-base-content/40">—</span>;
      if (inv.status !== "open")
        return (
          <span class="text-base-content/60">{formatDay(inv.due_date)}</span>
        );
      const days = daysUntil(inv.due_date);
      return (
        <div>
          <div>{formatDay(inv.due_date)}</div>
          <div
            class={[
              "text-xs",
              days < 0
                ? "font-medium text-error"
                : days <= 7
                  ? "text-warning"
                  : "text-base-content/50",
            ].join(" ")}
          >
            {days < 0
              ? t("inventory.invoices.overdue_by").replace(
                  "{days}",
                  String(-days),
                )
              : days === 0
                ? t("inventory.invoices.due_today")
                : t("inventory.invoices.due_in").replace(
                    "{days}",
                    String(days),
                  )}
          </div>
        </div>
      );
    };

    const statusBadge = (inv: InvoiceSummary) => {
      if (inv.status === "paid")
        return (
          <span class="badge badge-success badge-sm">
            {t("inventory.invoices.status_paid")}
          </span>
        );
      if (inv.status === "void")
        return (
          <span class="badge badge-ghost badge-sm">
            {t("inventory.invoices.status_void")}
          </span>
        );
      if (inv.is_overdue)
        return (
          <span class="badge badge-error badge-sm">
            {t("inventory.invoices.status_overdue")}
          </span>
        );
      return (
        <span class="badge badge-warning badge-outline badge-sm">
          {t("inventory.invoices.status_open")}
        </span>
      );
    };

    const renderTable = () => (
      <div class="overflow-x-auto rounded-lg border border-base-300 bg-base-100">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>{t("inventory.purchases.supplier")}</th>
              <th>{t("inventory.invoices.number")}</th>
              <th>{t("inventory.invoices.issue_date")}</th>
              <th>{t("inventory.invoices.due_date")}</th>
              <th class="text-right">{t("inventory.invoices.total")}</th>
              <th>{t("inventory.purchases.col_status")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {invoices.value.map((inv) => (
              <tr
                key={inv.id}
                class="cursor-pointer hover:bg-base-200/60"
                onClick={() => (editing.value = inv.id)}
              >
                <td>
                  <div class="font-medium">
                    {inv.supplier_name ?? (
                      <span class="text-base-content/40">—</span>
                    )}
                  </div>
                  {inv.receipt_count > 0 && (
                    <div class="text-xs text-base-content/50">
                      {t("inventory.invoices.delivery_count").replace(
                        "{count}",
                        String(inv.receipt_count),
                      )}
                    </div>
                  )}
                </td>
                <td>
                  <span class="inline-flex items-center gap-1">
                    {inv.invoice_number ?? (
                      <span class="text-base-content/40">—</span>
                    )}
                    {inv.has_document && (
                      <span
                        class="material-symbols-outlined text-sm text-base-content/40"
                        title={t("inventory.scan.has_document")}
                      >
                        attach_file
                      </span>
                    )}
                  </span>
                </td>
                <td>{formatDay(inv.issue_date)}</td>
                <td>{dueCell(inv)}</td>
                <td class="text-right font-medium tabular-nums">
                  {money(inv.total, inv.currency)}
                </td>
                <td>{statusBadge(inv)}</td>
                <td class="text-right">
                  {inv.status === "open" && (
                    <button
                      class="btn btn-ghost btn-xs"
                      onClick={(e: MouseEvent) => {
                        e.stopPropagation();
                        paying.value = inv;
                      }}
                    >
                      <span class="material-symbols-outlined text-base">
                        payments
                      </span>
                      {t("inventory.invoices.mark_paid")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    return () => (
      <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
        <SubpageHeader
          title={t("inventory.nav.invoices")}
          subtitle={t("inventory.invoices.subtitle")}
        >
          <div class="flex gap-2">
            <button
              class="btn btn-primary btn-sm"
              onClick={() => (scanning.value = true)}
            >
              <span class="material-symbols-outlined text-base">
                document_scanner
              </span>
              {t("inventory.scan.scan_invoice")}
            </button>
            <button
              class="btn btn-neutral btn-sm"
              onClick={() => {
                draft.value = null;
                editing.value = null;
              }}
            >
              <span class="material-symbols-outlined text-base">add</span>
              {t("inventory.invoices.new")}
            </button>
          </div>
        </SubpageHeader>

        <div class="flex-1 space-y-3 overflow-auto p-3">
          <SummaryTiles
            totals={totals.value}
            filter={filter.value}
            onFilter={(f: InvoiceFilter) => (filter.value = f)}
          />
          <div role="tablist" class="tabs tabs-boxed inline-flex bg-base-100">
            {FILTERS.map((f) => (
              <button
                key={f ?? "all"}
                role="tab"
                class={[
                  "tab tab-sm",
                  filter.value === f ? "tab-active" : "",
                ].join(" ")}
                onClick={() => (filter.value = f)}
              >
                {t(`inventory.invoices.filter_${f ?? "all"}`)}
              </button>
            ))}
          </div>
          {loading.value ? (
            <div class="flex justify-center p-10">
              <Loading />
            </div>
          ) : invoices.value.length === 0 ? (
            <div class="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <span class="material-symbols-outlined text-5xl text-base-content/30">
                request_quote
              </span>
              <p class="text-sm text-base-content/60">
                {t("inventory.invoices.empty")}
              </p>
            </div>
          ) : (
            renderTable()
          )}
        </div>

        {scanning.value && (
          <ScanDialog
            kind="invoice"
            onClose={() => (scanning.value = false)}
            onOpen={onOpen}
          />
        )}
        {editing.value !== undefined && (
          <InvoiceEditor
            invoiceId={editing.value}
            initial={draft.value}
            onClose={async (changed: boolean) => {
              editing.value = undefined;
              draft.value = null;
              if (changed) await load();
            }}
          />
        )}
        {paying.value && (
          <MarkPaidModal
            total={paying.value.total}
            currency={paying.value.currency}
            saving={busy.value}
            onClose={() => (paying.value = null)}
            onConfirm={pay}
          />
        )}
      </div>
    );
  },
});
